import { useTranslation } from 'react-i18next'

import { Camera, Mail, MessageCircle, Mic } from 'lucide-react'

import { cn } from '@/lib/utils'

const FLOAT_SHADOW = 'shadow-[0_18px_40px_oklch(0%_0_0/0.5)]'

/**
 * WhatsApp price-rise alert sent by Tipi (mirrors the fields of the real template:
 * ingredient, previous and new price, monthly impact). Sits bottom-right of a card pair.
 */
export function TipiPriceAlertCard({ className }: { className?: string }) {
    const { t } = useTranslation()

    return (
        <div
            className={cn(
                'absolute bottom-0 right-0 w-[244px] rotate-[4deg] overflow-hidden rounded-[18px] border border-tipi-border bg-tipi-surface',
                FLOAT_SHADOW,
                className,
            )}
        >
            <div className='flex items-center gap-2 bg-tipi-shell px-3 py-2'>
                <span className='flex size-[26px] items-center justify-center rounded-full bg-tipi-flame text-[11px] font-extrabold text-[oklch(98%_0.01_60)]'>
                    T
                </span>
                <span className='flex flex-1 flex-col gap-px'>
                    <span className='text-xs font-bold'>Tipi</span>
                </span>
                <MessageCircle size={16} className='text-tipi-green' />
            </div>
            <div className='flex flex-col items-start p-2.5'>
                <div className='flex max-w-[94%] flex-col gap-1 rounded-xl rounded-bl-[4px] bg-tipi-surface-up px-2.5 pb-[5px] pt-2'>
                    <span className='text-xs font-bold leading-[1.35]'>
                        {t('whyTipiMocks.alert.title', '⚠️ Subida de precio')}
                    </span>
                    <span className='text-xs leading-[1.4]'>
                        {t('whyTipiMocks.alert.body', 'Mero amarillo: de 23,00 € a 28,50 €/kg.')}
                    </span>
                    <span className='text-xs font-semibold leading-[1.4] text-tipi-flame'>
                        {t('whyTipiMocks.alert.impact', 'Te cuesta unos 96 € más al mes.')}
                    </span>
                    <span className='self-end text-[9px] text-tipi-muted'>08:15</span>
                </div>
            </div>
        </div>
    )
}

const WAVE_HEIGHTS = [6, 12, 8, 16, 10, 18, 7, 13, 9, 15, 6, 11] as const

/**
 * Capture step: a photographed delivery note being read, and a dictated recipe.
 * Delivery note sits top-left, voice bubble bottom-right.
 */
export function TipiCaptureCard({ className }: { className?: string }) {
    const { t } = useTranslation()
    const lines = [
        t('whyTipiMocks.capture.line1', 'Mero amarillo · 4,2 kg'),
        t('whyTipiMocks.capture.line2', 'Gamba roja · 3 kg'),
        t('whyTipiMocks.capture.line3', 'Lubina · 5 kg'),
    ]

    return (
        <div className={cn('absolute inset-0', className)}>
            <div
                className={cn(
                    'absolute left-0 top-0 flex w-[236px] -rotate-[4deg] flex-col gap-2 rounded-2xl bg-tipi-cream px-3.5 pb-3 pt-3 text-tipi-cream-fg',
                    FLOAT_SHADOW,
                )}
            >
                <div className='flex items-center gap-2'>
                    <Camera size={14} className='shrink-0 text-tipi-flame' />
                    <span className='text-[11px] font-bold uppercase tracking-[0.06em]'>
                        {t('whyTipiMocks.capture.docTitle', 'Albarán · Makro Pescado')}
                    </span>
                </div>
                <ul className='border-tipi-cream-fg/15 m-0 flex list-none flex-col gap-1 border-t p-0 pt-2'>
                    {lines.map(line => (
                        <li key={line} className='text-xs font-semibold leading-snug'>
                            {line}
                        </li>
                    ))}
                </ul>
                <div className='flex items-center gap-1.5'>
                    <span className='flex items-center gap-1.5 rounded-full bg-tipi-flame px-2 py-0.5 text-[10px] font-bold text-[oklch(98%_0.01_60)]'>
                        <span className='size-1.5 rounded-full bg-current motion-safe:animate-pulse' />
                        {t('whyTipiMocks.capture.reading', 'Leyendo albarán…')}
                    </span>
                    <span className='border-tipi-cream-fg/25 flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold'>
                        <Mail size={10} />
                        {t('whyTipiMocks.capture.email', 'Por email')}
                    </span>
                </div>
            </div>

            <div
                className={cn(
                    'absolute bottom-0 right-0 flex w-[252px] rotate-[3deg] flex-col gap-2 rounded-2xl border border-tipi-border bg-tipi-surface-up p-3',
                    FLOAT_SHADOW,
                )}
            >
                <div className='flex items-center gap-2.5'>
                    <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-tipi-flame text-[oklch(98%_0.01_60)]'>
                        <Mic size={14} />
                    </span>
                    <span className='flex h-5 items-center gap-[3px]'>
                        {WAVE_HEIGHTS.map((height, index) => (
                            <span key={index} className='w-[3px] rounded-full bg-tipi-flame' style={{ height }} />
                        ))}
                    </span>
                </div>
                <span className='text-xs leading-[1.4]'>
                    {t(
                        'foodCost.howItWorks.demo.inputQuote',
                        '"Tipi, crea una receta de croquetas de jamón con 200 gramos de jamón ibérico, 1 litro de leche…"',
                    )}
                </span>
            </div>
        </div>
    )
}
